import { Typography } from "antd";

type VisibilityCondition = {
  variable: string;
  equals: string;
};

type HeadingBlock = {
  type: "heading";
  text: string;
  level?: 1 | 2 | 3 | 4 | 5;
  visibleWhen?: VisibilityCondition;
};

type ParagraphBlock = {
  type: "paragraph";
  text: string;
  visibleWhen?: VisibilityCondition;
};

type ImageGridImage = {
  src: string;
  alt: string;
  caption?: string;
};

type ImageGridBlock = {
  type: "imageGrid";
  images: ImageGridImage[];
  minSize?: number;
  visibleWhen?: VisibilityCondition;
};

export type ContentBlock = HeadingBlock | ParagraphBlock | ImageGridBlock;

type ThankYouContentProps = {
  blocks: ContentBlock[];
  variables?: Record<string, unknown>;
};

const isBlockVisible = (
  block: ContentBlock,
  variables: Record<string, unknown>,
): boolean => {
  if (!block.visibleWhen) return true;

  return (
    String(variables[block.visibleWhen.variable]) === block.visibleWhen.equals
  );
};

// Fallback text shown when a token's value is missing, so drivers never see
// a raw `{variableName}` token in the thank-you copy.
const TOKEN_FALLBACKS: Record<string, string> = {
  servicePartnerName: "de hersteller",
  appointmentDateTime: "het gekozen moment",
};

// Interpolates `{variableName}` tokens in block text with values from `variables`.
const interpolate = (
  text: string,
  variables: Record<string, unknown>,
): string =>
  text.replace(/\{(\w+)\}/g, (match, variableName: string) => {
    const value = variables[variableName];

    if (value !== undefined && value !== null) return String(value);

    return TOKEN_FALLBACKS[variableName] ?? match;
  });

export const ThankYouContent = ({
  blocks,
  variables = {},
}: ThankYouContentProps) => {
  return (
    <div
      style={{ fontSize: 15, fontWeight: 500, color: "#555", width: "100%" }}
    >
      {blocks
        .filter((block) => isBlockVisible(block, variables))
        .map((block, index) => {
          switch (block.type) {
            case "heading":
              return (
                <Typography.Title
                  key={index}
                  level={block.level ?? 3}
                  style={{ marginBottom: 16 }}
                >
                  {interpolate(block.text, variables)}
                </Typography.Title>
              );
            case "paragraph":
              return (
                <Typography.Paragraph
                  key={index}
                  style={{
                    color: "#666",
                    lineHeight: "1.8rem",
                    marginBottom: "2rem",
                  }}
                >
                  {interpolate(block.text, variables)}
                </Typography.Paragraph>
              );
            case "imageGrid":
              return (
                <div
                  key={index}
                  style={{
                    display: "grid",
                    gridTemplateColumns: `repeat(auto-fit, minmax(${block.minSize ?? 160}px, 1fr))`,
                    gap: 16,
                    maxWidth: 900,
                    margin: "2rem auto",
                  }}
                >
                  {block.images.map((image) => (
                    <div key={image.src}>
                      <img
                        src={image.src}
                        alt={image.alt}
                        style={{ width: "100%", borderRadius: 8 }}
                      />
                      {image.caption && (
                        <p
                          style={{
                            color: "#999",
                            fontSize: ".8rem",
                            margin: ".5rem 0",
                          }}
                        >
                          {image.caption}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              );
            default:
              return null;
          }
        })}
    </div>
  );
};
